let cartRecsRoot = null;

/**
 * Initialize the cart recommendations grid
 */
function initCartRecommendationsGrid() {
  const container = document.getElementById("cart-recommendations");
  if (!container) {
    console.error(
      "[CartRecs] #cart-recommendations not found in DOM. Check cart.liquid."
    );
    return;
  }

  // Clean up previous instance if it exists
  if (cartRecsRoot) {
    cartRecsRoot.unmount();
    container.innerHTML = "";
  }

  try {
    cartRecsRoot = ReactDOM.createRoot(container);
    cartRecsRoot.render(React.createElement(CartRecommendationsGrid));
  } catch (error) {
    console.error("[CartRecs] Error rendering grid:", error);
  }
}

// -------------------------------
// Main grid component
// -------------------------------
function CartRecommendationsGrid() {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [cartItemCount, setCartItemCount] = React.useState(0);
  const [loadingHeading, setLoadingHeading] = React.useState(true);

  const scrollContainerRef = React.useRef(null);
  const limit = 5;

  // On mount: fetch cart => load recs
  React.useEffect(() => {
    const load = async () => {
      const cartData = await fetchCartItemCount();
      setLoadingHeading(false);
      await loadRecommendations(cartData);

      // Ensure empty cart elements are properly shown/hidden
      updateEmptyCartDisplay(cartData.items?.length || 0);
    };
    load().catch((error) =>
      console.error("[CartRecs] Error in useEffect load:", error)
    );
  }, []);

  // Update empty cart visibility
  function updateEmptyCartDisplay(itemCount) {
    const noItemsElement = document.querySelector(".cart--no-items");
    const continueShoppingTop = document.querySelector(
      ".cart--continue-shopping-top"
    );
    const continueShoppingBottom = document.querySelector(
      ".cart--continue-shopping-bottom"
    );

    if (itemCount === 0) {
      // Show empty cart message (which contains the continue_shopping_t link)
      if (noItemsElement) {
        noItemsElement.style.display = "block";
      }
      // Hide both top and bottom continue shopping links (only for full cart)
      if (continueShoppingTop) {
        continueShoppingTop.style.display = "none";
      }
      if (continueShoppingBottom) {
        continueShoppingBottom.style.display = "none";
      }
    } else {
      // Hide empty cart message
      if (noItemsElement) {
        noItemsElement.style.display = "none";
      }
      // Show both continue shopping links (for full cart)
      if (continueShoppingTop) {
        continueShoppingTop.style.display = "block";
      }
      if (continueShoppingBottom) {
        continueShoppingBottom.style.display = "block";
      }
    }
  }

  // Once products are loaded, initialize the theme's QuickAdd (if present)
  React.useEffect(() => {
    if (!loading && products.length > 0) {
      if (
        window.ProductQuickAdd &&
        typeof window.ProductQuickAdd.init === "function"
      ) {
        window.ProductQuickAdd.init();
      }
    }
  }, [loading, products]);

  // Reset scroll position
  React.useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [loading, products]);

  // --------------------------------
  // 1) Fetch cart data from /cart.js
  // --------------------------------
  async function fetchCartItemCount() {
    try {
      const cartRes = await fetch("/cart.js");
      if (cartRes.ok) {
        const data = await cartRes.json();
        const itemCount =
          data.item_count !== undefined
            ? data.item_count
            : data.items?.length || 0;
        setCartItemCount(itemCount);
        return data;
      }
    } catch (err) {
      console.error("[CartRecs] fetchCartItemCount error:", err);
      setCartItemCount(0);
      return { items: [] };
    }
  }

  // --------------------------------
  // 2) Load recommendations
  // --------------------------------
  window.loadRecommendations = async function loadRecommendations(cartData)  {
    setLoading(true);

    try {
      const cartItems = cartData.items || [];
      const cartProductIds = new Set(cartItems.map((i) => i.product_id));

      // If cart truly empty
      if (cartItems.length === 0) {
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // Cart has items => pick the first item
      const firstItem = cartItems[0];
      if (!firstItem) {
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      const productUrl = document.querySelector(".cart--item a")?.href;
      const productHandle = productUrl?.split("/products/")[1]?.split("?")[0];

      if (!productHandle) {
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // Attempt up to 3 fetch tries
      let productData;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const productRes = await fetch(`/products/${productHandle}.js`);
          if (!productRes.ok) {
            if (attempt === 2) throw new Error("Max retries reached");
            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
            continue;
          }
          productData = await productRes.json();
          break;
        } catch (err) {
          console.error(
            "[CartRecs] Error fetching product data attempt",
            attempt + 1,
            ":",
            err
          );
        }
      }
      if (!productData || !productData.id) {
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // Now fetch Shopify's built-in recs
      const recUrl = `/recommendations/products.json?product_id=${
        productData.id
      }&limit=${Math.max(1, Math.min(limit, 10))}`;
      const recResp = await fetch(recUrl, { credentials: "same-origin" });
      if (!recResp.ok) {
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }
      const recData = await recResp.json();

      if (recData.products && recData.products.length > 0) {
        const recommendedProducts = await Promise.all(
          recData.products.map((p) => fetchFullProductData(p))
        );
        const uniqueProducts = recommendedProducts
          .filter(
            (p) =>
              p &&
              !cartProductIds.has(p.id) &&
              p.title !== "Goat Milk Subscription Box"
          )
          .slice(0, 4);
        setProducts(uniqueProducts);
      } else {
        await loadDefaultRecommendations(cartProductIds);
      }
    } catch (err) {
      console.error("[CartRecs] Error loading recs:", err);
      setProducts([]);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  // --------------------------------
  // 3) Load default recs if empty/fallback
  // --------------------------------
  async function loadDefaultRecommendations(cartProductIds) {
    // Try best-sellers collection first
    const recUrl = `/collections/best-sellers-1/products.json?limit=${Math.max(
      1,
      Math.min(limit, 10)
    )}`;
    try {
      const recResp = await fetch(recUrl, { credentials: "same-origin" });

      if (!recResp.ok) {
        console.error("[CartRecs] best-sellers fetch error:", recResp.status);
      } else {
        const recData = await recResp.json();

        if (recData.products && recData.products.length > 0) {
          const recommendedProducts = await Promise.all(
            recData.products.map((p) => fetchFullProductData(p))
          );

          let uniqueProducts = recommendedProducts
            .filter((p) => {
              if (!p) return false;
              if (cartProductIds.has(p.id)) return false;
              if (p.title === "Goat Milk Subscription Box") return false;
              return true;
            })
            .slice(0, 4);

          if (uniqueProducts.length > 0) {
            setProducts(uniqueProducts);
            return;
          }
        }
      }

      // True fallback: If best-sellers empty/fails, fetch from all collections
      const fallbackUrl = `/collections/all/products.json?limit=${Math.max(
        1,
        Math.min(limit, 10)
      )}`;
      const fallbackResp = await fetch(fallbackUrl, {
        credentials: "same-origin",
      });

      if (!fallbackResp.ok) {
        console.error(
          "[CartRecs] all products fallback error:",
          fallbackResp.status
        );
        setProducts([]);
        return;
      }

      const fallbackData = await fallbackResp.json();

      if (fallbackData.products && fallbackData.products.length > 0) {
        const fallbackProducts = await Promise.all(
          fallbackData.products.map((p) => fetchFullProductData(p))
        );

        const uniqueProducts = fallbackProducts
          .filter((p) => {
            if (!p) return false;
            if (cartProductIds.has(p.id)) return false;
            if (p.title === "Goat Milk Subscription Box") return false;
            return true;
          })
          .slice(0, 4);

        setProducts(uniqueProducts);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error("[CartRecs] Error fetching default recs:", err);
      setProducts([]);
    }
  }

  // --------------------------------
  // 4) Fetch full product data and extract real Judge.me ratings
  // --------------------------------
  async function fetchFullProductData(product) {
    try {
      const fullRes = await fetch(`/products/${product.handle}.js`);
      if (!fullRes.ok) throw new Error("Failed to fetch full product data");
      const fullData = await fullRes.json();

      // Get Judge.me review data from JSON-LD structured data
      let rating = 0;
      let reviewCount = 0;

      try {
        // Fetch the product page and extract JSON-LD structured data
        const productPageResponse = await fetch(`/products/${product.handle}`, {
          credentials: "same-origin",
          headers: {
            Accept: "text/html",
          },
        });

        if (productPageResponse.ok) {
          const productPageHtml = await productPageResponse.text();

          // Look for JSON-LD script tag with aggregateRating
          const jsonLdMatch = productPageHtml.match(
            /<script[^>]*type="application\/ld\+json"[^>]*>(.*?)<\/script>/gs
          );

          if (jsonLdMatch) {
            for (const scriptMatch of jsonLdMatch) {
              try {
                const jsonContent = scriptMatch
                  .replace(/<script[^>]*>/i, "")
                  .replace(/<\/script>/i, "");
                const jsonData = JSON.parse(jsonContent);

                // Check if this JSON-LD contains aggregateRating (direct or in @graph)
                let ratingData = null;

                if (jsonData.aggregateRating) {
                  // Direct aggregateRating
                  ratingData = jsonData.aggregateRating;
                } else if (jsonData["@graph"]) {
                  // Look for Product or ProductGroup type in @graph array
                  for (const item of jsonData["@graph"]) {
                    if (
                      (item["@type"] === "Product" ||
                        item["@type"] === "ProductGroup") &&
                      item.aggregateRating
                    ) {
                      ratingData = item.aggregateRating;
                      break;
                    }
                  }
                }

                if (ratingData) {
                  rating = parseFloat(ratingData.ratingValue) || 0;
                  reviewCount = parseInt(ratingData.reviewCount) || 0;
                  break;
                }
              } catch (parseError) {
                continue;
              }
            }
          }
        }

        // Fallback: check for existing JSON-LD on current page
        if (rating === 0) {
          const existingJsonLd = document.querySelectorAll(
            'script[type="application/ld+json"]'
          );

          for (const script of existingJsonLd) {
            try {
              const jsonData = JSON.parse(script.textContent);

              if (
                jsonData.aggregateRating &&
                (jsonData.name
                  ?.toLowerCase()
                  .includes(product.title.toLowerCase()) ||
                  jsonData["@id"]?.includes(product.handle))
              ) {
                rating = parseFloat(jsonData.aggregateRating.ratingValue) || 0;
                reviewCount =
                  parseInt(jsonData.aggregateRating.reviewCount) || 0;
                break;
              }
            } catch (parseError) {
              continue;
            }
          }
        }
      } catch (error) {
        // Ratings failed to load, continue without them
      }

      return {
        id: product.id,
        handle: product.handle,
        url: `/products/${product.handle}`,
        title: product.title,
        price: formatPrice(fullData.price),
        compareAtPrice: fullData.compare_at_price
          ? formatPrice(fullData.compare_at_price)
          : null,
        image:
          fullData.featured_image ||
          (fullData.images && fullData.images[0]) ||
          "/api/placeholder/300/300",
        secondaryImage:
          fullData.images && fullData.images[1] ? fullData.images[1] : null,
        available: fullData.available,
        hasOnlyDefaultVariant:
          fullData.variants && fullData.variants.length === 1,
        variants: fullData.variants || [],
        // Real Judge.me rating data
        rating: rating,
        reviewCount: reviewCount,
        maxRating: 5,
      };
    } catch (err) {
      console.error("[CartRecs] Error fetching full product data:", err);
      return null;
    }
  }

  // --------------------------------
  // 5) Quick add rendering
  // --------------------------------
  function renderQuickAdd(product) {
    if (!product.available) return null;

    const buttonText = product.hasOnlyDefaultVariant
      ? "Quick Add"
      : "Choose Options";
    const props = {
      className: "product--quick-add",
      "aria-busy": isUpdating ? "true" : "false",
      "data-handle": product.handle,
      "data-form": product.hasOnlyDefaultVariant ? "false" : "true",
      style: {
        position: "absolute",
        bottom: "0",
        right: "0",
        padding: "var(--spacing--1)",
      },
    };

    if (product.hasOnlyDefaultVariant && product.variants?.length) {
      props["data-id"] = product.variants[0].id;
    }

    return React.createElement(
      "product-quick-add",
      props,
      React.createElement(
        "button",
        {
          className: "product-quick-add--button",
          type: "button",
          "aria-haspopup": "menu",
          "aria-label": buttonText,
          style: { zIndex: 21, position: "relative" },
        },
        React.createElement(
          "div",
          { className: "product-quick-add--icon" },
          React.createElement(
            "svg",
            {
              className: "icon--root icon--plus",
              width: "10",
              height: "10",
              viewBox: "0 0 10 10",
              xmlns: "http://www.w3.org/2000/svg",
            },
            React.createElement("path", {
              d: "M10 5.714H5.714V10H4.286V5.714H0V4.286h4.286V0h1.428v4.286H10z",
              fill: "#000",
              "fill-rule": "nonzero",
            })
          )
        ),
        React.createElement(
          "div",
          { className: "product-quick-add--text", "data-item": "small-text" },
          buttonText
        ),
        React.createElement("div", {
          className: "product--spinner",
          style: { display: isUpdating ? "block" : "none" },
        })
      )
    );
  }

  // --------------------------------
  // 6) Format price helper
  // --------------------------------
  function formatPrice(cents) {
    if (!cents || isNaN(cents)) return "$0.00";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: Shopify.currency.active,
    }).format(cents / 100);
  }

  // --------------------------------
  // 7) Render stars with quarter/half/three-quarter precision
  // --------------------------------
  function renderStars(rating, reviewCount, maxRating = 5) {
    if (!reviewCount || reviewCount === 0) return null;

    const stars = [];

    for (let i = 1; i <= maxRating; i++) {
      const difference = rating - (i - 1);
      let starClass = "empty-star";
      let starSymbol = "☆";

      if (difference >= 1) {
        // Full star
        starClass = "full-star";
        starSymbol = "★";
      } else if (difference >= 0.75) {
        // Three-quarter star
        starClass = "three-quarter-star";
        starSymbol = "★";
      } else if (difference >= 0.5) {
        // Half star
        starClass = "half-star";
        starSymbol = "★";
      } else if (difference >= 0.25) {
        // Quarter star
        starClass = "quarter-star";
        starSymbol = "★";
      }

      stars.push(
        React.createElement(
          "span",
          {
            key: `star-${i}`,
            className: starClass,
            style: {
              position: "relative",
              display: "inline-block",
            },
          },
          starSymbol
        )
      );
    }

    // Review count
    stars.push(
      React.createElement(
        "span",
        { key: "count", className: "review-count" },
        ` (${reviewCount})`
      )
    );

    return React.createElement("div", { className: "product--reviews" }, stars);
  }

  // --------------------------------
  // 8) Render the grid
  // --------------------------------
  return React.createElement(
    "div",
    {
      className: "cart--recommendations",
      style: {
        borderTop: "1px solid var(--color--border)",
        marginTop: "16px",
        paddingTop: "16px",
      },
    },
    !loadingHeading &&
      React.createElement(
        "h3",
        {
          "data-item": "section-heading",
          style: {
            fontSize: "18px",
            marginBottom: cartItemCount > 0 ? "16px" : "24px",
            paddingLeft: "16px",
          },
        },
        cartItemCount > 0 ? "Just For You" : "Discover More"
      ),
    React.createElement(
      "div",
      {
        ref: scrollContainerRef,
        className: "cart--recommendations--items",
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          overflowX: "hidden",
          gap: "16px",
          padding: "0 16px 16px",
          paddingTop: cartItemCount > 0 ? "8px" : "16px",
          minHeight: "200px",
        },
      },
      loading
        ? React.createElement(
            "div",
            { style: { padding: "16px", textAlign: "center" } },
            ""
          )
        : products.length > 0
        ? products.map((product) =>
            React.createElement(
              "div",
              {
                key: product.id,
                className: "product-card--root",
                style: {
                  flex: "none",
                  width: "100%",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                },
              },
              React.createElement(
                "a",
                {
                  href: product.url,
                  className: "product-card--image-wrapper",
                  style: {
                    display: "block",
                    position: "relative",
                    paddingBottom: "100%",
                    overflow: "hidden",
                  },
                },
                React.createElement("img", {
                  src: product.image,
                  alt: product.title,
                  className: "product-card--image",
                  style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  },
                  onError: (e) => {
                    e.target.src = "/api/placeholder/300/300";
                  },
                }),
                product.secondaryImage &&
                  React.createElement("img", {
                    src: product.secondaryImage,
                    alt: product.title + " - Second View",
                    className: "product-card--hover-image",
                    style: {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    },
                    onError: (e) => {
                      e.target.style.display = "none";
                    },
                  }),
                renderQuickAdd(product)
              ),
              React.createElement(
                "div",
                {
                  className: "product-card--details",
                  style: {
                    padding: "6px 8px 8px 8px",
                    textAlign: "left",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "52px", // Reduced from 60px
                  },
                },
                React.createElement(
                  "h4",
                  {
                    className: "product--title",
                    style: {
                      marginBottom: "2px", // Tightened spacing
                      fontSize: "14px",
                      lineHeight: "1.2",
                      fontWeight: "normal",
                      textTransform: "uppercase",
                      minHeight: "30px", // Reduced from 34px
                    },
                  },
                  React.createElement(
                    "a",
                    {
                      href: product.url,
                      style: {
                        color: "inherit",
                        textDecoration: "none",
                      },
                      onMouseEnter: (e) => {
                        e.target.style.textDecoration = "underline";
                      },
                      onMouseLeave: (e) => {
                        e.target.style.textDecoration = "none";
                      },
                    },
                    product.title
                  )
                ),
                // Stars positioned between title and price with tight spacing
                React.createElement(
                  "div",
                  {
                    style: {
                      marginBottom: "2px", // Tight spacing to price
                    },
                  },
                  renderStars(
                    product.rating,
                    product.reviewCount,
                    product.maxRating
                  )
                ),
                React.createElement(
                  "div",
                  {
                    className: "product--price",
                    style: {
                      fontSize: "14px",
                      marginTop: "auto",
                    },
                  },
                  product.compareAtPrice &&
                    React.createElement(
                      "s",
                      {
                        style: {
                          color: "var(--color--body--light)",
                          marginRight: "8px",
                        },
                      },
                      product.compareAtPrice
                    ),
                  React.createElement(
                    "span",
                    { className: "money" },
                    product.price
                  )
                )
              )
            )
          )
        : React.createElement(
            "div",
            { style: { padding: "16px", textAlign: "center" } },
            "No products available."
          )
    )
  );
}

// -------------------------------
// On DOM load, init once
// -------------------------------
document.addEventListener("DOMContentLoaded", () => {
  initCartRecommendationsGrid();
});

// If theme triggers "theme:cart:updated", we re-init
let debounceTimer;
window.addEventListener("theme:cart:updated", () => {
  // Immediately update empty cart display to prevent flash
  fetch("/cart.js")
    .then((response) => response.json())
    .then((data) => {
      const itemCount =
        data.item_count !== undefined
          ? data.item_count
          : data.items?.length || 0;
      updateEmptyCartDisplayImmediate(itemCount);

      // Aggressively scroll to cart items when items are added
      if (itemCount > 0) {
        // Multiple scroll attempts with different timings
        const scrollToCartItems = () => {
          const cartHeader = document.querySelector(".cart--header");
          const cartTitle = document.querySelector(".cart--title");
          const cartBody = document.querySelector(".cart--body");
          const cartDrawer = document.querySelector(
            '.drawer--container[data-view="cart-drawer"]'
          );

          // Try multiple scroll targets in order of preference
          if (cartTitle) {
            cartTitle.scrollIntoView({ behavior: "smooth", block: "start" });
          } else if (cartHeader) {
            cartHeader.scrollIntoView({ behavior: "smooth", block: "start" });
          } else if (cartBody) {
            cartBody.scrollIntoView({ behavior: "smooth", block: "start" });
          } else if (cartDrawer) {
            cartDrawer.scrollTo({ top: 0, behavior: "smooth" });
          }
        };

        // Immediate scroll
        scrollToCartItems();

        // Backup scroll after slight delay
        setTimeout(scrollToCartItems, 100);
      }
    })
    .catch(() => {});

  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    initCartRecommendationsGrid();

    if (
      window.ProductQuickAdd &&
      typeof window.ProductQuickAdd.init === "function"
    ) {
      window.ProductQuickAdd.init();
    }

    // Final aggressive scroll to ensure we stay at the cart items
    setTimeout(() => {
      const cartTitle = document.querySelector(".cart--title");
      const cartHeader = document.querySelector(".cart--header");
      const cartDrawer = document.querySelector(
        '.drawer--container[data-view="cart-drawer"]'
      );

      if (cartTitle) {
        cartTitle.scrollIntoView({ behavior: "auto", block: "start" });
      } else if (cartHeader) {
        cartHeader.scrollIntoView({ behavior: "auto", block: "start" });
      } else if (cartDrawer) {
        cartDrawer.scrollTo({ top: 0, behavior: "auto" });
      }
    }, 50);
  }, 150); // Reduced from 200ms to be faster
});

// Immediate update function to prevent flashing
function updateEmptyCartDisplayImmediate(itemCount) {
  const noItemsElement = document.querySelector(".cart--no-items");
  const continueShoppingTop = document.querySelector(
    ".cart--continue-shopping-top"
  );
  const continueShoppingBottom = document.querySelector(
    ".cart--continue-shopping-bottom"
  );

  if (itemCount === 0) {
    // Show empty cart message (which contains the continue_shopping_t link)
    if (noItemsElement) {
      noItemsElement.style.display = "block";
    }
    // Hide both top and bottom continue shopping links (only for full cart)
    if (continueShoppingTop) {
      continueShoppingTop.style.display = "none";
    }
    if (continueShoppingBottom) {
      continueShoppingBottom.style.display = "none";
    }
  } else {
    // Hide empty cart message
    if (noItemsElement) {
      noItemsElement.style.display = "none";
    }
    // Show both continue shopping links (for full cart)
    if (continueShoppingTop) {
      continueShoppingTop.style.display = "block";
    }
    if (continueShoppingBottom) {
      continueShoppingBottom.style.display = "block";
    }
  }
}
